import fileDownload from 'js-file-download'

import { jsToFormData, paginatedTransform, rawTransform } from 'src/services/skuit/utils'
import {
  Attachment,
  Csv,
  CustomField,
  Flag,
  FreightOrder,
  FreightOrderBarcodedUnit,
  FreightOrderPallet,
  FreightOrderUnit,
  FreightShipment,
  FreightShipmentBarcodedUnit,
  FreightShipmentPallet,
  FreightShipmentUnit,
  PaginatedResponse,
  Truckload,
  TruckloadSM,
  TruckloadSMShipment,
} from '../../api/models'

import { API, CustomAxiosConfig } from './types'

export interface ExportProps {
  fields: string[]
  guid?: string
}


class OrderCustomFields {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<CustomField>> => {
    const response = await this.api.get('/freight/orders/custom-fields', config)
    return paginatedTransform(CustomField)(response.data)
  }

  create = async (data: any = {}, config: CustomAxiosConfig = {}): Promise<CustomField> => {
    const response = await this.api.post('/freight/orders/custom-fields', data, config)
    return rawTransform(CustomField)(response.data)
  }
}

class OrderCustomFieldValues {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<CustomField>> => {
    const response = await this.api.get('/freight/order-custom-field-values', config)
    return paginatedTransform(CustomField)(response.data)
  }

  create = async (data: any = {}, config: CustomAxiosConfig = {}): Promise<CustomField> => {
    const response = await this.api.post('/freight/order-custom-field-values', data, config)
    return rawTransform(CustomField)(response.data)
  }
}

class OrderFlags {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Flag>> => {
    const response = await this.api.get('/freight/orders/flags', config)
    return paginatedTransform(Flag)(response.data)
  }
}

class OrderBarcodedUnits {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<FreightOrderBarcodedUnit>> => {
    const response = await this.api.get('/freight/order-barcoded-units', config)
    return paginatedTransform(FreightOrderBarcodedUnit)(response.data)
  }
}

class OrderAttachments {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (guid: string, config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Attachment>> => {
    const response = await this.api.get(`/freight/orders/${guid}/attachments`, config)
    return paginatedTransform(Attachment)(response.data)
  }

  create = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<any> => {
    const axiosConfig = {
      ...config,
      headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
    }
    return await this.api.post(
      `/freight/orders/${guid}/attachments`,
      jsToFormData(data),
      axiosConfig
    )
  }
}

class OrderUnits {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<FreightOrderUnit>> => {
    const response = await this.api.get('/freight/order-units', config)
    return paginatedTransform(FreightOrderUnit)(response.data)
  }
}

class OrderPallets {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<FreightOrderPallet>> => {
    const response = await this.api.get('/freight/order-pallets', config)
    return paginatedTransform(FreightOrderPallet)(response.data)
  }
}

class Orders {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<FreightOrder>> => {
    const response = await this.api.get('/freight/orders', config)
    return paginatedTransform(FreightOrder)(response.data)
  }

  retrieve = async (guid: string, config: CustomAxiosConfig = {}): Promise<FreightOrder> => {
    const response = await this.api.get(`/freight/orders/${guid}`, config)
    return rawTransform(FreightOrder)(response.data)
  }

  create = async (data: any, config: CustomAxiosConfig = {}): Promise<FreightOrder> => {
    const response = await this.api.post('/freight/orders', data, config)
    return rawTransform(FreightOrder)(response.data)
  }

  patch = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<FreightOrder> => {
    const response = await this.api.patch(`/freight/orders/${guid}`, data, config)
    return rawTransform(FreightOrder)(response.data)
  }

  skuAvailability = async (data: any, config: CustomAxiosConfig = {}) => {
    config.headers = { 'Content-Type': 'multipart/form-data', ...config.headers }
    const response = await this.api.post('/freight/orders/sku-availability/csv', data, config)
    return response
  }

  palletAvailability = async (data: any, config: CustomAxiosConfig = {}) => {
    config.headers = { 'Content-Type': 'multipart/form-data', ...config.headers }
    const response = await this.api.post('/freight/orders/pallet-availability/csv', data, config)
    return response
  }

  autoFulfill = async (guid: string, config: CustomAxiosConfig = {}) => {
    const response = await this.api.post(`/freight/orders/${guid}/auto-fulfill`, {}, config)
    return response
  }

  uploadCsv = async (config?: CustomAxiosConfig) => {
    const axiosConfig = {
      ...config,
      method: 'post' as 'post',
      url: '/freight/orders/csv',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: jsToFormData(config?.data),
      customTransform: rawTransform(Csv),
    }
    return await this.api.axiosInstance(axiosConfig)
  }

  export = async ({ fields }: ExportProps) => {
    const params = { fields: fields.join(',') }
    const response = await this.api.axiosInstance({
      url: '/freight/orders/export',
      params,
      responseType: 'blob',
    })
    fileDownload(response.data, 'orders.csv')
  }

  exportReport = async ({ fields, guid }: ExportProps) => {
    const params = { fields: fields.join(',') }
    const response = await this.api.axiosInstance({
      url: `/freight/orders/${guid}/inventory/export`,
      params,
      responseType: 'blob',
    })
    fileDownload(response.data, 'orderReport.csv')
  }

  get customFields() {
    const api = new OrderCustomFields(this.api)
    Object.defineProperty(this, 'customFields', { value: api })
    return api
  }

  get customFieldValues() {
    const api = new OrderCustomFieldValues(this.api)
    Object.defineProperty(this, 'customFieldValues', { value: api })
    return api
  }

  get attachments() {
    const api = new OrderAttachments(this.api)
    Object.defineProperty(this, 'attachments', { value: api })
    return api
  }

  get pallets() {
    const api = new OrderPallets(this.api)
    Object.defineProperty(this, 'pallets', { value: api })
    return api
  }

  get units() {
    const api = new OrderUnits(this.api)
    Object.defineProperty(this, 'units', { value: api })
    return api
  }

  get barcodedUnits() {
    const api = new OrderBarcodedUnits(this.api)
    Object.defineProperty(this, 'barcodedUnits', { value: api })
    return api
  }

  get flags() {
    const api = new OrderFlags(this.api)
    Object.defineProperty(this, 'flags', { value: api })
    return api
  }
}

class ShipmentAttachments {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (guid: string, config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Attachment>> => {
    const response = await this.api.get(`/freight/shipments/${guid}/attachments`, config)
    return paginatedTransform(Attachment)(response.data)
  }

  create = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<any> => {
    const axiosConfig = {
      ...config,
      headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
    }
    return await this.api.post(
      `/freight/shipments/${guid}/attachments`,
      jsToFormData(data),
      axiosConfig
    )
  }
}

class ShipmentBarcodedUnits {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (
    config: CustomAxiosConfig = {}
  ): Promise<PaginatedResponse<FreightShipmentBarcodedUnit>> => {
    const response = await this.api.get('/freight/shipment-barcoded-units', config)
    return paginatedTransform(FreightShipmentBarcodedUnit)(response.data)
  }
}

class ShipmentPallets {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<FreightShipmentPallet>> => {
    const response = await this.api.get('/freight/shipment-pallets', config)
    return paginatedTransform(FreightShipmentPallet)(response.data)
  }
}

class ShipmentUnits {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<FreightShipmentUnit>> => {
    const response = await this.api.get('/freight/shipment-units', config)
    return paginatedTransform(FreightShipmentUnit)(response.data)
  }

  export = async ({ fields }: ExportProps, extraParams?: Record<string, string | number>) => {
    const params = { fields: fields.join(','), ...extraParams }
    const response = await this.api.axiosInstance({ url: '/freight/shipment-units/export', params, responseType: 'blob' })
    fileDownload(response.data, 'shipment_inventory.csv')
  }
}

class Shipments {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<FreightShipment>> => {
    config.params = { ...config.params }
    const response = await this.api.get('/freight/shipments', config)
    return paginatedTransform(FreightShipment)(response.data)
  }

  create = async (data: any, config: CustomAxiosConfig = {}): Promise<FreightShipment> => {
    const response = await this.api.post('/freight/shipments', data, config)
    return rawTransform(FreightShipment)(response.data)
  }

  patch = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<FreightShipment> => {
    const response = await this.api.patch(`/freight/shipments/${guid}`, data, config)
    return rawTransform(FreightShipment)(response.data)
  }

  retrieve = async (guid: string, config: CustomAxiosConfig = {}): Promise<FreightShipment> => {
    const response = await this.api.get(`/freight/shipments/${guid}`, config)
    return rawTransform(FreightShipment)(response.data)
  }

  packingSlip = async (
    guid: string,
    config: CustomAxiosConfig = {}
  ): Promise<TruckloadSMShipment> => {
    const response = await this.api.get(`/freight/shipments/${guid}/packing-slip`, config)
    return rawTransform(TruckloadSMShipment)(response.data)
  }

  export = async ({ fields }: ExportProps) => {
    const params = { fields: fields.join(',') }
    const response = await this.api.axiosInstance({ url: '/freight/shipments/export', params, responseType: 'blob' })
    fileDownload(response.data, 'shipments.csv')
  }

  get attachments() {
    const api = new ShipmentAttachments(this.api)
    Object.defineProperty(this, 'attachments', { value: api })
    return api
  }

  get barcodedUnits() {
    const api = new ShipmentBarcodedUnits(this.api)
    Object.defineProperty(this, 'barcodedUnits', { value: api })
    return api
  }

  get pallets() {
    const api = new ShipmentPallets(this.api)
    Object.defineProperty(this, 'pallets', { value: api })
    return api
  }

  get units() {
    const api = new ShipmentUnits(this.api)
    Object.defineProperty(this, 'units', { value: api })
    return api
  }
}

class TruckloadAttachments {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (guid: string, config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Attachment>> => {
    const response = await this.api.get(`/freight/truckloads/${guid}/attachments`, config)
    return paginatedTransform(Attachment)(response.data)
  }

  create = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<any> => {
    const axiosConfig = {
      ...config,
      headers: { ...config?.headers, 'Content-Type': 'multipart/form-data' },
    }
    return await this.api.post(
      `/freight/truckloads/${guid}/attachments`,
      jsToFormData(data),
      axiosConfig
    )
  }
}

export class Truckloads {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Truckload>> => {
    config.params = { ...config.params }
    const response = await this.api.get('/freight/truckloads', config)
    return paginatedTransform(Truckload)(response.data)
  }

  create = async (data: any, config: CustomAxiosConfig = {}): Promise<Truckload> => {
    const response = await this.api.post('/freight/truckloads', data, config)
    return rawTransform(Truckload)(response.data)
  }

  patch = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<Truckload> => {
    const response = await this.api.patch(`/freight/truckloads/${guid}`, data, config)
    return rawTransform(Truckload)(response.data)
  }

  retrieve = async (guid: string, config: CustomAxiosConfig = {}): Promise<Truckload> => {
    const response = await this.api.get(`/freight/truckloads/${guid}`, config)
    return rawTransform(Truckload)(response.data)
  }

  shippingManifest = async (guid: string, config: CustomAxiosConfig = {}): Promise<TruckloadSM> => {
    const response = await this.api.get(`/freight/truckloads/${guid}/shipping-manifest`, config)
    return rawTransform(TruckloadSM)(response.data)
  }

  get attachments() {
    const api = new TruckloadAttachments(this.api)
    Object.defineProperty(this, 'attachments', { value: api })
    return api
  }
}

export class Freight {
  api: API

  constructor(api: API) {
    this.api = api
  }

  get orders() {
    const api = new Orders(this.api)
    Object.defineProperty(this, 'orders', { value: api })
    return api
  }

  get shipments() {
    const api = new Shipments(this.api)
    Object.defineProperty(this, 'shipments', { value: api })
    return api
  }

  get truckloads() {
    const api = new Truckloads(this.api)
    Object.defineProperty(this, 'truckloads', { value: api })
    return api
  }
}
